import { createReducer } from '@reduxjs/toolkit';
import { ChannelsState, Channel } from '../types/channels';
import { logout } from '../types';
import {
  fetchChannels,
  fetchChannelsForOrg,
  switchChannel,
} from '../actions/channels';
import { switchOrganization } from '../actions/organizations';
import {
  fetchChannelAbilities,
  fetchChannelRevisionAbilities,
} from '../actions/abilities';

const storedActiveChannelId = localStorage.getItem('activeChannelId');

const initialState: ChannelsState = {
  channelsByOrg: {},
  loadingByOrg: {},
  fetchedByOrg: {},
  isChannelLoading: false,
  activeChannelId: storedActiveChannelId
    ? parseInt(storedActiveChannelId, 10)
    : null,
  abilityRulesByChannel: {},
  revisionAbilityRulesByChannel: {},
  abilityRulesFetchedByChannel: {},
};

export const channelsReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(fetchChannels.pending, (state) => {
      state.isChannelLoading = true;
    })
    .addCase(fetchChannels.fulfilled, (state, action) => {
      state.isChannelLoading = false;

      // NOTE: This is legacy fetchChannels - will be replaced with fetchChannelsForOrg
      // For now, store in a temporary location until migration is complete
      const channelsMap: Record<string, Channel> = {};
      action.payload.forEach((channel) => {
        channelsMap[String(channel.channel.id)] = channel;
      });

      // TODO: Remove this once all code migrated to use channelsByOrg
      // Temporarily store in orgId 0 for backward compatibility during migration
      state.channelsByOrg[0] = channelsMap;
      state.fetchedByOrg[0] = true;

      const currentActiveChannel = state.activeChannelId
        ? channelsMap[state.activeChannelId]
        : null;
      if (action.payload.length > 0 && !currentActiveChannel) {
        const newActiveChannelId = action.payload[0].channel.id;
        state.activeChannelId = newActiveChannelId;
        localStorage.setItem('activeChannelId', String(newActiveChannelId));
      }
    })
    .addCase(fetchChannels.rejected, (state) => {
      state.isChannelLoading = false;
    })
    // Fetch channels for organization - pending
    .addCase(fetchChannelsForOrg.pending, (state, action) => {
      state.isChannelLoading = true;
      state.loadingByOrg[action.meta.arg] = true;
    })
    // Fetch channels for organization - fulfilled
    .addCase(fetchChannelsForOrg.fulfilled, (state, action) => {
      const { orgId, channels } = action.payload;
      const channelArray = Array.isArray(channels) ? channels : [];

      // Convert array to Record keyed by channel ID
      const channelsRecord: Record<string, Channel> = {};
      channelArray.forEach((ch: any) => {
        if (ch.channel && ch.channel.id) {
          // New shape: { channel: {...}, role: 'OWNER' }
          channelsRecord[ch.channel.id.toString()] = ch;
        } else if (ch.id) {
          // Fallback: flat channel object
          channelsRecord[ch.id.toString()] = {
            channel: ch,
            role: ch.role || 'MEMBER',
          };
        }
      });

      // Store in nested structure
      state.channelsByOrg[orgId] = channelsRecord;
      state.loadingByOrg[orgId] = false;
      state.fetchedByOrg[orgId] = true;
      state.isChannelLoading = false;

      // Auto-select first channel if none active
      if (!state.activeChannelId && channelArray.length > 0) {
        const first = channelArray[0] as any;
        const firstId = first.channel?.id || first.id;
        if (firstId) {
          state.activeChannelId = firstId;
          localStorage.setItem('activeChannelId', firstId.toString());
        }
      }
    })
    // Fetch channels for organization - rejected
    .addCase(fetchChannelsForOrg.rejected, (state, action) => {
      const orgId = action.meta.arg;
      state.isChannelLoading = false;
      state.loadingByOrg[orgId] = false;
      // Still mark as fetched to prevent infinite refetching on error
      // (e.g. PENDING/REJECTED membership or stale activeOrganizationId).
      state.fetchedByOrg[orgId] = true;
    })
    .addCase(switchChannel, (state, action) => {
      state.activeChannelId = action.payload;
      localStorage.setItem('activeChannelId', String(action.payload));
    })
    // Workspace switch: retarget active channel. If channels are cached,
    // pick first directly (fetchChannelsForOrg.condition skips re-fetch and
    // its fulfilled auto-pick never fires); otherwise clear and let
    // fulfilled auto-pick once the fetch resolves.
    .addCase(switchOrganization, (state, action) => {
      const orgId = action.payload;
      // Cached path picks the lowest-id channel (Object.values on a
      // numeric-string-keyed record iterates numerically). The cold path
      // (fetchChannelsForOrg.fulfilled) picks channelArray[0] from the API
      // response — different default. Acceptable for now since the user can
      // re-pick, but worth revisiting if the default-channel UX matters more.
      const cachedList = Object.values(state.channelsByOrg[orgId] ?? {});
      if (cachedList.length > 0) {
        const firstId = cachedList[0].channel.id;
        state.activeChannelId = firstId;
        localStorage.setItem('activeChannelId', String(firstId));
      } else {
        state.activeChannelId = null;
        localStorage.removeItem('activeChannelId');
      }
    })
    // Store CASL ability rules per channel
    .addCase(fetchChannelAbilities.fulfilled, (state, action) => {
      const { channelId, rules } = action.payload;
      state.abilityRulesByChannel[channelId] = rules;
      // Flag is shared between channel + revision scopes (they're fetched
      // together in ProtectedRoute). Setting it here gates the second-hop
      // dispatch on subsequent navigations.
      state.abilityRulesFetchedByChannel[channelId] = true;
    })
    // Store revision-scope CASL ability rules per channel
    .addCase(fetchChannelRevisionAbilities.fulfilled, (state, action) => {
      const { channelId, rules } = action.payload;
      state.revisionAbilityRulesByChannel[channelId] = rules;
    })
    .addCase(logout, (state) => {
      localStorage.removeItem('activeChannelId');
      localStorage.removeItem('channelDetails');
      state.activeChannelId = null;
      state.channelsByOrg = {};
      state.loadingByOrg = {};
      state.fetchedByOrg = {};
      state.isChannelLoading = false;
      state.abilityRulesByChannel = {};
      state.revisionAbilityRulesByChannel = {};
      state.abilityRulesFetchedByChannel = {};
    });
});
